import { useState, useCallback, useRef, Ref, useEffect } from "react";
import throttle from "lodash/throttle";
import { IS_SERVER } from "@/misc/constants";

const SHOW_BP = 50;
const SCROLL_AMOUNT = 200;
const SCROLL_INNACURACY = 50;
const SCROLL_DELAY = 200;

type tProps = {
  disabled?: boolean;
};

type tButtonsVisibilityState = {
  left: boolean;
  right: boolean;
};
type tReturnValue = {
  ref: Ref<Maybe<HTMLElement>>;
  visibleButtons: tButtonsVisibilityState;
  goPrev: tVoidFn;
  goNext: tVoidFn;
};
export const useScrollButtons = ({ disabled }: tProps = {}): tReturnValue => {
  const ref = useRef<Maybe<HTMLElement>>(null);
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const [visibleButtons, setVisibleButtons] = useState<tButtonsVisibilityState>(
    {
      left: false,
      right: false,
    },
  );
  const handleStateChange = useCallback(() => {
    const $el = ref.current;
    if (!$el || disabledRef.current) return;
    const width = $el.scrollWidth;
    const scroll = $el.scrollLeft;
    const visibleWidth = $el.getBoundingClientRect().width;

    setVisibleButtons({
      left: scroll > SHOW_BP,
      right: width - SHOW_BP > scroll + visibleWidth,
    });
  }, []);
  const resizeObs = useRef(
    IS_SERVER ? null : new ResizeObserver(handleStateChange),
  );
  const onScroll = useRef(throttle(handleStateChange, SCROLL_DELAY));
  const refCb = useCallback((node: Maybe<HTMLElement>) => {
    const oldNode = ref.current;
    if (oldNode) {
      oldNode.removeEventListener("scroll", onScroll.current);
      if (resizeObs.current) {
        resizeObs.current.unobserve(oldNode);
      }
    }
    ref.current = node;
    if (node) {
      node.addEventListener("scroll", onScroll.current);
      if (resizeObs.current) {
        resizeObs.current.observe(node);
      }
    }
  }, []);
  useEffect(
    () => () => {
      if (resizeObs.current) {
        resizeObs.current.disconnect();
      }
    },
    [],
  );
  const goPrev = useCallback(() => {
    const $el = ref.current;
    if (!$el) return;
    $el.scrollTo({
      left: $el.scrollLeft - SCROLL_AMOUNT,
      behavior: "smooth",
    });
  }, []);
  const goNext = useCallback(() => {
    const $el = ref.current;
    if (!$el) return;
    const width = $el.scrollWidth;
    const scroll = $el.scrollLeft;
    const scrollUntilEnd = $el.scrollWidth - $el.clientWidth - scroll;
    const shouldScrollTilEnd =
      SCROLL_AMOUNT + SCROLL_INNACURACY + scroll >= scrollUntilEnd;
    $el.scrollTo({
      left: shouldScrollTilEnd ? width : scroll + SCROLL_AMOUNT,
      behavior: "smooth",
    });
  }, [ref]);
  return {
    ref: refCb,
    visibleButtons,
    goPrev,
    goNext,
  };
};
